import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ConfirmationService, SelectItem, Table } from 'primeng';

import { UrlSegments, UserStatus } from '../constants/app.constants';
import { DetailsPageData, OverviewKeys, TabsData } from './employee-overview.constants';
import {
  AllTabsData,
  ApplicationResponseData,
  OverviewPendingApplications,
  RegisteredChimneySweepers,
  TechnicianApplicationData,
  TechnicianDetailsData,
  TechnicianResponseData,
} from './employee-overview.domain';
import { EmployeeOverviewService } from './employee-overview.service';
import { ErrorService } from '../services/error/error.service';
import { NetzTranslateService } from '../services/netz-translate.service';
import { NotificationsService } from '../services/notifications/notifications.service';
import { Router } from '@angular/router';
import { LocalStorageService } from '../services/local-storage.service';
import { TechnicianData, UpdateLockStatusResponse } from '../domains/common.domain';
import { CommonService } from '../services/common.service';
import { LocationStrategy } from '@angular/common';
import { EnvironmentService } from '../services/environment.service';
import { DestroyableComponent } from '../common/destroyable.component';

@Component({
  providers: [ConfirmationService, EmployeeOverviewService],
  selector: 'app-employee-overview',
  styleUrls: ['./employee-overview.component.scss'],
  templateUrl: './employee-overview.component.html',
})
export class EmployeeOverviewComponent extends DestroyableComponent implements OnInit {
  allTabsData: AllTabsData = TabsData;
  currentTabIndex: number = 0;
  detailsData: TechnicianDetailsData = {
    allowEdit: false,
    formData: null,
    isReadOnly: false,
    staticData: DetailsPageData,
    tabIndex: -1,
  };
  displayDetails: boolean = false;
  filterFields: {
    tabOne: string[];
    tabTwo: string[];
    tabThree: string[];
    tabFour: string[];
    tabFive: string[];
  };
  isLoading: boolean;
  allPermissions: SelectItem[];
  role: string;
  selectedPermissionFilters: string[] = [];
  selectedRow: number = -1;
  showOptionsPanel: boolean = false;
  tabValue: string = this.environmentService.standardTechnicianEnabled ? OverviewKeys.TabOne : OverviewKeys.TabTwo;
  technicianId: string;
  tabDataRequestUrl: string;

  allTabsResponseData: {
    tabOne: TechnicianData[];
    tabTwo: TechnicianData[];
    tabThree: TechnicianApplicationData[];
    tabFour: TechnicianApplicationData[];
    tabFive: RegisteredChimneySweepers[];
  } = {
    tabOne: [],
    tabTwo: [],
    tabThree: null,
    tabFour: [],
    tabFive: [],
  };

  showRegisteredChimneySweepersTab: boolean = this.environmentService.showRegisteredChimneySweepersTab;

  @ViewChild('tabOne') private tableOne: Table;
  @ViewChild('tabTwo') private tableTwo: Table;
  @ViewChild('tabThree') private tableThree: Table;
  @ViewChild('tabFour') private tableFour: Table;
  @ViewChild('tabFive') private tableFive: Table;

  constructor(
    private cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private confirmationService: ConfirmationService,
    private employeeOverviewService: EmployeeOverviewService,
    private errorService: ErrorService,
    private netzTranslateService: NetzTranslateService,
    private notificationsService: NotificationsService,
    private router: Router,
    public environmentService: EnvironmentService,
    locationStrategy: LocationStrategy,
    localStorageService: LocalStorageService
  ) {
    super();
    locationStrategy.onPopState(() => this.reloadTab());

    this.role = localStorageService.getItem('role');
    this.technicianId = localStorageService.getItem('technician_id');
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.privateFilterFields();
    this.initializeFilters();
    this.prepareColumnFilterData();
    this.getTabsData(
      this.environmentService.standardTechnicianEnabled ? UrlSegments.activeStandardTechnicians : UrlSegments.activeGuestTechnicians
    );

    this.preventLeak(this.netzTranslateService.registerForLanguageChange()).subscribe(() => this.initializeFilters());

    this.registerForViewNotification();
    this.hideWaterCommodityIfNeeded();
  }

  changeTab(tabIndex: number): void {
    this.displayDetails = false;
    this.isLoading = true;

    if (tabIndex !== this.currentTabIndex) {
      const tablesMap: { [key: number]: Table } = {
        0: this.tableOne,
        1: this.tableTwo,
        2: this.tableThree,
        3: this.tableFour,
        4: this.tableFive,
      };

      tablesMap[this.currentTabIndex]?.reset();

      this.currentTabIndex = this.environmentService.standardTechnicianEnabled ? tabIndex : tabIndex + 1;
    }

    this.tabValue =
      this.currentTabIndex === 0
        ? OverviewKeys.TabOne
        : this.currentTabIndex === 1
        ? OverviewKeys.TabTwo
        : this.currentTabIndex === 2
        ? OverviewKeys.TabThree
        : this.currentTabIndex === 3
        ? OverviewKeys.TabFour
        : OverviewKeys.TabFive;

    this.allTabsData[this.tabValue].data = [];

    switch (this.currentTabIndex) {
      case 0:
        this.tabDataRequestUrl = UrlSegments.activeStandardTechnicians;
        break;
      case 1:
        this.tabDataRequestUrl = UrlSegments.activeGuestTechnicians;
        break;
      case 2:
        this.tabDataRequestUrl = UrlSegments.submittedTechnicianApplications;
        break;
      case 3:
        this.tabDataRequestUrl = UrlSegments.rejectedTechnicians;
        break;
      case 4:
        this.tabDataRequestUrl = UrlSegments.registeredChimneySweepers;
        break;
    }

    this.getTabsData(this.tabDataRequestUrl);
  }

  showDetails(rowIndex: number, technicianId: number, editData: boolean = false): void {
    history.pushState(null, null, window.location.href);
    this.selectedRow = rowIndex;

    const idProperty = [2, 3].indexOf(this.currentTabIndex) > -1 ? 'technicianId' : 'companyId';
    const selectedApplication = this.allTabsData[this.tabValue].data.filter(data => {
      return data[idProperty] === technicianId;
    });
    if (
      selectedApplication.length &&
      ([0, 1, 2].indexOf(this.currentTabIndex) > -1 ||
        (this.currentTabIndex === 3 &&
          (!editData || (editData && (this.technicianId === selectedApplication[0].lockedby || selectedApplication[0].locked === '0')))))
    ) {
      this.detailsData.allowEdit = editData;
      this.detailsData.tabIndex = this.currentTabIndex;
      this.prepareDetailsFormData(technicianId);
    } else {
      this.confirmApplicationEdit();
    }
  }

  confirmDelete(technicianId: number): void {
    this.confirmationService.confirm({
      accept: () => {
        this.deleteUser(technicianId);
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  parseTableValues(field: string, value: any): any {
    if (field === 'telephone') {
      if (value.length > 25) {
        return `${value.slice(0, 24)}...`;
      }
      return value;
    }
    return value;
  }

  evaluateDetailsButtonState(rowData: TechnicianApplicationData, column: string): boolean {
    return rowData[column] || (rowData.locked === '1' && this.technicianId !== rowData.lockedby);
  }

  errorHandler = (): void => {
    this.isLoading = false;
    this.prepareTabsData([]);
  };

  updateProcessingStatus(selectedApplication: OverviewPendingApplications): void {
    this.isLoading = true;

    this.preventLeak(
      this.commonService.updateApplicationLockStatus(selectedApplication.locked === '0', selectedApplication.taId, null)
    ).subscribe(
      (response: UpdateLockStatusResponse) => {
        if (response?.technician_application) {
          this.getTabsData(this.tabDataRequestUrl);
        } else {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);
        }
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  sendMessage(technicianId: number): void {
    this.router.navigate(['/employee-overview/messages', technicianId]);
  }

  private getTabsData(technicianParam: string): void {
    if ([2, 3].indexOf(this.currentTabIndex) > -1) {
      this.preventLeak(this.employeeOverviewService.getPendingApplications(technicianParam)).subscribe(
        (response: ApplicationResponseData) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true, this.errorHandler);
          if (response.technician_application) {
            this.prepareTabsData(response.technician_application);
          }
        },
        error => {
          this.allTabsData.tabThree.data = [];
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );

      return;
    }

    this.preventLeak(this.employeeOverviewService.getTechniciansData(technicianParam)).subscribe(
      (response: TechnicianResponseData) => {
        this.errorService.throwErrorIfFailure(response, true, this.errorHandler);
        if (response.technician) {
          this.prepareTabsData(response.technician);
          this.isLoading = false;
        }
      },
      error => {
        const currentTabName = this.currentTabIndex === 0 ? OverviewKeys.TabOne : OverviewKeys.TabThree;
        this.allTabsData[currentTabName].data = [];
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private deleteUser(technicianId: number): void {
    this.preventLeak(this.employeeOverviewService.updateTechnicianStatus(technicianId, UserStatus.DELETED)).subscribe(
      response => {
        this.errorService.throwErrorIfFailure(response, true);
        this.allTabsResponseData[this.tabValue] = this.allTabsResponseData[this.tabValue].filter(
          data => Number(data.technician_id) !== technicianId
        );

        this.allTabsData[this.tabValue].data = this.allTabsData[this.tabValue].data.filter(data => data.companyId === technicianId);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private privateFilterFields(): void {
    this.filterFields = {
      tabOne: [],
      tabTwo: [],
      tabThree: [],
      tabFour: [],
      tabFive: [],
    };
    Object.keys(this.allTabsData).forEach(key => {
      this.allTabsData[key].columns.forEach(dataItem => {
        this.filterFields[key].push(dataItem.field);
      });
    });
  }

  private prepareColumnFilterData(): void {
    this.commonService.initializeCustomFilter(this.filterCustomColumns);
  }

  private filterCustomColumns = (value: string[], filter: string[]): boolean => {
    const permissions = value.filter(obj => filter.indexOf(obj) > -1);
    return permissions.length === filter.length;
  };

  private initializeFilters(): void {
    this.allPermissions = ['gas', 'water', 'power', 'heating'].map(productType => ({
      label: this.netzTranslateService.translateString(`overview.details-data.data-rows.${productType}`),
      value: productType,
    }));
  }

  private prepareTabsData(responseData: TechnicianData[] | TechnicianApplicationData[]): void {
    if (this.currentTabIndex === 2 || this.currentTabIndex === 3) {
      this.allTabsResponseData[this.tabValue] = responseData as TechnicianApplicationData[];
    } else {
      this.allTabsResponseData[this.tabValue] = responseData as TechnicianData[];
    }
    this.allTabsData[this.tabValue].data = this.employeeOverviewService.parseTabData(this.currentTabIndex, responseData);
  }

  private prepareDetailsFormData(technicianId: number, selectedApplication: OverviewPendingApplications = null): void {
    this.detailsData.isReadOnly = [OverviewKeys.TabThree, OverviewKeys.TabFour].indexOf(this.tabValue) === -1;
    if ([OverviewKeys.TabThree, OverviewKeys.TabFour].indexOf(this.tabValue) > -1) {
      const selectedTechnician = this.allTabsResponseData[this.tabValue].filter(data => {
        if (selectedApplication && Number(data.technician_id) === technicianId) {
          data.locked = selectedApplication.locked;
          data.lockedby = selectedApplication.lockedby;
        }
        return Number(data.technician_id) === technicianId;
      });
      this.detailsData.formData = selectedTechnician.length && selectedTechnician[0];
      this.updateOnReadApplication();
    } else {
      this.isLoading = true;
      this.preventLeak(this.employeeOverviewService.getTechnicianApplication(technicianId)).subscribe(
        (response: ApplicationResponseData) => {
          this.detailsData.formData = response.technician_application && response.technician_application[0];
          this.isLoading = false;
          this.updateOnReadApplication();
        },
        () => {
          this.isLoading = false;
        }
      );
    }
  }

  private registerForViewNotification(): void {
    this.preventLeak(this.notificationsService.registerForViewNotification()).subscribe((data: any) => {
      if (data && data.role && data.role === 'employee') {
        if (this.currentTabIndex !== 2) {
          this.changeTab(2);
        }
      }
    });
  }

  private updateOnReadApplication(): void {
    if (this.detailsData.formData) {
      this.displayDetails = true;
      this.cdr.detectChanges();
      window.scroll(0, 0);
      /*if (this.currentTabIndex === 2) {
        const readSubscription = this.employeeOverviewService.updateApplicationViewStatus({});
      }*/
    }
  }

  private confirmApplicationEdit() {
    this.confirmationService.confirm({
      message: this.netzTranslateService.translateString('overview.edit-application-info-text'),
      acceptVisible: false,
      rejectVisible: false,
      key: 'applicationEditConfirmDialog',
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  private reloadTab() {
    this.changeTab(this.currentTabIndex);
  }

  private hideWaterCommodityIfNeeded() {
    if (this.environmentService.technicianRegistrationHideWaterCommodity) {
      var networkPermissions = this.detailsData.staticData.rightPanel.find(p => p.id == 'network-permissions');
      networkPermissions.data = networkPermissions.data.filter(d => d.field != 'water');
    }
  }
}
